import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaMicroscope, 
  FaChartLine, 
  FaFlask, 
  FaCheckCircle, 
  FaBoxOpen, 
  FaSearch, 
  FaIndustry, 
  FaTint, 
  FaFilter, 
  FaSun, 
  FaBolt, 
  FaVial, 
  FaGem, 
  FaClipboardList, 
  FaUsers,
  FaCheck
} from 'react-icons/fa';
import './Manufacturing.css';

const Manufacturing = () => {
  const qualityProcesses = [
    {
      icon: <FaMicroscope />,
      title: 'Raw Material Testing',
      description: 'Every batch of raw materials undergoes rigorous testing before processing'
    },
    {
      icon: <FaChartLine />,
      title: 'In-Process Control',
      description: 'Continuous monitoring at every stage of production'
    },
    {
      icon: <FaFlask />,
      title: 'Lab Testing',
      description: 'Complete microbiological and chemical analysis'
    },
    {
      icon: <FaCheckCircle />,
      title: 'Final Quality Check',
      description: 'Multiple quality checks before packaging'
    },
    {
      icon: <FaBoxOpen />,
      title: 'Packaging Integrity',
      description: 'Tamper-proof packaging with quality seals'
    },
    {
      icon: <FaSearch />,
      title: 'Batch Traceability',
      description: 'Complete tracking from source to delivery'
    }
  ];

  return (
    <div className="manufacturing-page">
      {/* Page Hero */}
      <section className="mfg-hero">
        <div className="hero-bg">
          <img src="/About Page images/manufacturing-facility.jpg" alt="Manufacturing Facility" />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="mfg-hero-content">
            <span className="hero-badge"><FaIndustry /> Our Facility</span>
            <h1>Manufacturing & Quality</h1>
            <p>State-of-the-art production with uncompromising quality standards</p>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Manufacturing</span>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Overview */}
      <section className="facility-overview section">
        <div className="container">
          <div className="overview-grid">
            <div className="overview-content">
              <span className="section-label">Our Facility</span>
              <h2>Modern Manufacturing Excellence</h2>
              <p>
                Our manufacturing facility in Bareilly, UP is equipped with 
                state-of-the-art machinery and follows international quality 
                standards. The fully automated production lines ensure minimal 
                human contact, maintaining the highest levels of hygiene.
              </p>
              <div className="facility-stats">
                <div className="fac-stat">
                  <span className="stat-value">25,000+</span>
                  <span className="stat-text">Sq. Ft. Area</span>
                </div>
                <div className="fac-stat">
                  <span className="stat-value">50,000</span>
                  <span className="stat-text">Bottles/Day Capacity</span>
                </div>
                <div className="fac-stat">
                  <span className="stat-value">100%</span>
                  <span className="stat-text">Automated</span>
                </div>
              </div>
            </div>
            <div className="overview-image">
              <img src="/Manufacturing & Quality images/bottling-machines.jpg" alt="Bottling Machines" />
            </div>
          </div>
        </div>
      </section>

      {/* Production Process */}
      <section className="production-process section bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Water Production Process</h2>
            <p>7-stage purification ensuring crystal clear, safe drinking water</p>
          </div>
          
          <div className="process-visual">
            <div className="process-flow">
              <div className="process-item">
                <div className="process-icon"><FaTint /></div>
                <div className="process-line"></div>
                <h4>Source Water</h4>
                <p>Deep bore-well from protected aquifers</p>
              </div>
              <div className="process-item">
                <div className="process-icon"><FaFilter /></div>
                <div className="process-line"></div>
                <h4>Pre-Treatment</h4>
                <p>Aeration and sedimentation tanks</p>
              </div>
              <div className="process-item">
                <div className="process-icon"><FaFilter /></div>
                <div className="process-line"></div>
                <h4>Sand Filtration</h4>
                <p>Multi-media sand filters</p>
              </div>
              <div className="process-item">
                <div className="process-icon"><FaFilter /></div>
                <div className="process-line"></div>
                <h4>Carbon Filter</h4>
                <p>Activated carbon absorption</p>
              </div>
              <div className="process-item">
                <div className="process-icon"><FaFilter /></div>
                <div className="process-line"></div>
                <h4>RO System</h4>
                <p>Reverse osmosis purification</p>
              </div>
              <div className="process-item">
                <div className="process-icon"><FaSun /></div>
                <div className="process-line"></div>
                <h4>UV Treatment</h4>
                <p>Ultraviolet sterilization</p>
              </div>
              <div className="process-item">
                <div className="process-icon"><FaBolt /></div>
                <div className="process-line"></div>
                <h4>Ozonization</h4>
                <p>Ozone disinfection</p>
              </div>
              <div className="process-item">
                <div className="process-icon"><FaBoxOpen /></div>
                <h4>Bottling</h4>
                <p>Hygienic automated filling</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="quality-assurance section">
        <div className="container">
          <div className="section-header">
            <h2>Quality Assurance</h2>
            <p>Rigorous quality control at every step of production</p>
          </div>
          
          <div className="qa-grid">
            {qualityProcesses.map((process, index) => (
              <div key={index} className="qa-card">
                <div className="qa-icon">{process.icon}</div>
                <h4>{process.title}</h4>
                <p>{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Lab */}
      <section className="quality-lab section bg-light">
        <div className="container">
          <div className="lab-grid">
            <div className="lab-image">
              <img src="/Manufacturing & Quality images/quality-lab.jpg" alt="Quality Lab" />
            </div>
            <div className="lab-content">
              <span className="section-label">In-House Laboratory</span>
              <h2>State-of-the-Art Quality Lab</h2>
              <p>
                Our in-house laboratory is equipped with advanced testing 
                equipment to conduct comprehensive quality checks on every 
                batch of production.
              </p>
              <div className="lab-tests">
                <div className="test-item">
                  <span className="test-icon"><FaVial /></span>
                  <div>
                    <h5>Microbiological Testing</h5>
                    <p>E.coli, Coliform, Total Plate Count</p>
                  </div>
                </div>
                <div className="test-item">
                  <span className="test-icon"><FaFlask /></span>
                  <div>
                    <h5>Chemical Analysis</h5>
                    <p>pH, TDS, Hardness, Chlorides</p>
                  </div>
                </div>
                <div className="test-item">
                  <span className="test-icon"><FaGem /></span>
                  <div>
                    <h5>Physical Testing</h5>
                    <p>Turbidity, Color, Odor, Taste</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hygiene Standards */}
      <section className="hygiene-section section">
        <div className="container">
          <div className="hygiene-grid">
            <div className="hygiene-content">
              <span className="section-label">Hygiene First</span>
              <h2>Strict Hygiene Protocols</h2>
              <p>
                Our facility maintains the highest hygiene standards with strict 
                protocols for workers, equipment, and environment to ensure 
                product safety and quality.
              </p>
              <ul className="hygiene-list">
                <li>
                  <span className="check"><FaCheck /></span>
                  <span>Daily sanitization of all production areas</span>
                </li>
                <li>
                  <span className="check"><FaCheck /></span>
                  <span>Mandatory PPE for all workers</span>
                </li>
                <li>
                  <span className="check"><FaCheck /></span>
                  <span>Air-filtered production environment</span>
                </li>
                <li>
                  <span className="check"><FaCheck /></span>
                  <span>Regular health checkups for staff</span>
                </li>
                <li>
                  <span className="check"><FaCheck /></span>
                  <span>Pest control management</span>
                </li>
                <li>
                  <span className="check"><FaCheck /></span>
                  <span>Clean-in-place (CIP) systems</span>
                </li>
              </ul>
            </div>
            <div className="hygiene-image">
              <img src="/Manufacturing & Quality images/hygiene-workers.jpg" alt="Hygiene Standards" />
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="mfg-certifications section bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Our Certifications</h2>
            <p>Recognized by leading national and international bodies</p>
          </div>
          
          <div className="cert-cards">
            <div className="cert-card-large">
              <img src="/Home Page images/bis-badge.png" alt="BIS" />
              <div className="cert-info">
                <h4>BIS IS 14543</h4>
                <p>Bureau of Indian Standards</p>
                <ul>
                  <li>Mandatory certification for packaged drinking water</li>
                  <li>Annual surveillance audits</li>
                  <li>Factory testing compliance</li>
                  <li>Product quality monitoring</li>
                </ul>
              </div>
            </div>
            <div className="cert-card-large">
              <img src="/logo & badge images/fssai-badge.png" alt="FSSAI" />
              <div className="cert-info">
                <h4>FSSAI License</h4>
                <p>Food Safety and Standards Authority of India</p>
                <ul>
                  <li>Complete compliance with food safety laws</li>
                  <li>Hygienic manufacturing practices</li>
                  <li>Safe packaging materials</li>
                  <li>Proper labeling requirements</li>
                </ul>
              </div>
            </div>
            <div className="cert-card-large">
              <img src="/logo & badge images/iso-badge.png" alt="ISO" />
              <div className="cert-info">
                <h4>ISO 22000:2018</h4>
                <p>International Organization for Standardization</p>
                <ul>
                  <li>Food safety management system</li>
                  <li>HACCP principles implementation</li>
                  <li>Continuous improvement processes</li>
                  <li>International recognition</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Tour */}
      <section className="facility-tour section">
        <div className="container">
          <div className="tour-content">
            <div className="tour-text">
              <h2>Visit Our Facility</h2>
              <p>
                We welcome business partners and potential distributors to 
                visit our manufacturing facility. See our production process, 
                quality systems, and meet our team.
              </p>
              <div className="tour-features">
                <div className="tour-feature">
                  <span><FaIndustry /></span>
                  <span>Production Line Tour</span>
                </div>
                <div className="tour-feature">
                  <span><FaMicroscope /></span>
                  <span>Quality Lab Visit</span>
                </div>
                <div className="tour-feature">
                  <span><FaUsers /></span>
                  <span>Meet the Team</span>
                </div>
                <div className="tour-feature">
                  <span><FaClipboardList /></span>
                  <span>Certificate Verification</span>
                </div>
              </div>
              <div className="tour-buttons">
                <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center', width: '100%'}}>
                  <a 
                    href="https://wa.me/918077894559?text=Hello, I would like to schedule a facility tour."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Schedule via +91 8077894559
                  </a>
                  <a 
                    href="https://wa.me/917983864997?text=Hello, I would like to schedule a facility tour."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Schedule via +91 79838 64997
                  </a>
                </div>
                <Link to="/contact" className="btn btn-secondary">
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="tour-image">
              <img src="/About Page images/manufacturing-facility.jpg" alt="Facility Tour" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mfg-cta">
        <div className="container">
          <div className="mfg-cta-content">
            <h2>Quality You Can Trust</h2>
            <p>Partner with a manufacturer committed to excellence</p>
            <div className="mfg-cta-buttons">
              <Link to="/distributor" className="btn btn-white">
                Become Distributor
              </Link>
              <Link to="/contact" className="btn btn-outline-light">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Manufacturing;
